import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import {
    Loader2,
    CheckCircle2,
    XCircle,
    Pencil,
    ShieldAlert,
    Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Approvals() {
    const { currentBusiness } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("pending");

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        try {
            const { approvals } = await api.approvals(currentBusiness.id, tab);
            setItems(approvals || []);
        } catch {
            toast.error("Couldn't load approvals.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id, tab]);

    return (
        <div className="max-w-4xl mx-auto space-y-6 fade-in" data-testid="approvals-page">
            <div>
                <div className="label-eyebrow text-amber-300">Approval Inbox</div>
                <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                    Decide what the AI publishes.
                </h1>
                <p className="mt-2 text-sm text-slate-400">
                    Every sensitive action routes here — approve, edit, or reject.
                </p>
            </div>

            <div className="flex items-center gap-2">
                {["pending", "approved", "rejected", "edited"].map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        data-testid={`approvals-tab-${t}`}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                            tab === t
                                ? "bg-amber-500/15 border-amber-500/30 text-amber-200"
                                : "bg-white/[0.02] border-white/10 text-slate-400 hover:text-slate-100 hover:bg-white/[0.05]"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-center py-16 text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </div>
            ) : items.length === 0 ? (
                <div
                    className="surface rounded-2xl p-10 text-center text-slate-500"
                    data-testid="approvals-empty"
                >
                    <Inbox className="h-8 w-8 mx-auto mb-3 text-slate-600" />
                    Nothing here — inbox zero.
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map((a) => (
                        <ApprovalItem key={a.id} a={a} onChange={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

// What each approval type edits, and what context to show next to it.
const TYPES = {
    review_reply: { label: "Review reply", field: "reply_text", publish: true },
    comment_reply: { label: "Instagram comment reply", field: "reply_text", publish: true },
    whatsapp_reply: { label: "WhatsApp reply", field: "reply_text", publish: true },
    audit_fix: { label: "Audit fix", field: "proposed_value", current: "current_value" },
    seo_fix: { label: "SEO fix", field: "proposed", current: "current" },
    aeo_fix: { label: "AEO answer", field: "proposed_answer" },
    profile_fix: { label: "Profile fix", field: "proposed_value", fallback: "ai_prompt" },
};

function proposedText(a) {
    const cfg = TYPES[a.type] || { field: "reply_text" };
    const p = a.proposed_payload || {};
    return p[cfg.field] || (cfg.fallback && p[cfg.fallback]) || "";
}

function Context({ a }) {
    const p = a.proposed_payload || {};
    const box = (label, body) => (
        <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 text-xs text-slate-400">
            <span className="mono uppercase text-[10px] tracking-widest text-slate-500 block mb-1">{label}</span>
            <div className="text-slate-200 whitespace-pre-wrap break-words">{body}</div>
        </div>
    );
    if (a.type === "comment_reply") return box(`Original comment · @${p.author || "unknown"}`, p.text);
    if (a.type === "whatsapp_reply")
        return box(`Customer message · ${p.customer_phone || ""}${p.language ? ` · ${p.language}` : ""}`, p.incoming_text);
    if (a.type === "seo_fix")
        return box(`Issue${p.sample_url ? ` · ${p.sample_url}` : ""}`, p.issue_title || a.subject_summary);
    if (a.type === "aeo_fix")
        return box("Customer question", `${p.question || ""}${p.proposed_heading ? `\nSuggested heading: ${p.proposed_heading}` : ""}`);
    if (a.type === "profile_fix")
        return box(`${(p.platform || "").replace("_", " ")} · ${p.item || ""}`, p.draft_source || "AI draft");
    return null;
}

function ApprovalItem({ a, onChange }) {
    const cfg = TYPES[a.type] || { label: a.type.replaceAll("_", " "), field: "reply_text" };
    const p = a.proposed_payload || {};
    const [text, setText] = useState(proposedText(a));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setText(proposedText(a));
    }, [a]);

    const decide = async (action) => {
        setBusy(true);
        try {
            const edited = action === "edit" ? { ...p, [cfg.field]: text } : undefined;
            const res = await api.decideApproval(a.id, action, edited);
            if (action === "reject") toast.success("Rejected.");
            else toast.success(res.applied_state || (cfg.publish ? "Published." : "Approved."));
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `Couldn't ${action}.`);
        } finally {
            setBusy(false);
        }
    };

    const isPending = a.status === "pending";
    const editable = isPending && !a.is_stale;
    const typeColor = cfg.publish
        ? "bg-indigo-500/15 border border-indigo-500/30 text-indigo-200"
        : "bg-cyan-500/15 border border-cyan-500/30 text-cyan-200";
    const editor = (
        <Textarea
            data-testid={`approval-textarea-${a.id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={a.type === "aeo_fix" || a.type === "profile_fix" ? 7 : 4}
            disabled={!editable}
            className="bg-white/[0.02] border-indigo-500/20 text-sm text-slate-100 placeholder:text-slate-500"
        />
    );

    return (
        <div
            data-testid={`approval-item-${a.id}`}
            className="surface rounded-2xl p-5 space-y-4"
        >
            <div className="flex items-center gap-2 flex-wrap">
                <Badge className={typeColor}>{cfg.label}</Badge>
                <span className="text-sm font-semibold text-slate-100 min-w-0 break-words">
                    {a.subject_summary}
                </span>
                {p.provenance && (
                    <Badge
                        variant="outline"
                        className="border-indigo-500/30 bg-indigo-500/10 text-indigo-200 font-mono text-[10px] uppercase tracking-widest"
                    >
                        {p.provenance}
                    </Badge>
                )}
                {a.is_stale && (
                    <Badge className="bg-rose-500/15 border border-rose-500/30 text-rose-200">
                        Stale (&gt;7 days)
                    </Badge>
                )}
                <Badge
                    variant="outline"
                    className="ml-auto border-white/10 text-slate-400 capitalize"
                >
                    {a.status}
                </Badge>
            </div>

            {a.reason && (
                <div className="rounded-xl bg-amber-500/[0.05] border border-amber-500/20 p-3 text-xs text-amber-200 flex items-start gap-2">
                    <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <div>{a.reason}</div>
                </div>
            )}

            <Context a={a} />

            {cfg.current ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="min-w-0">
                        <div className="label-eyebrow text-slate-500 mb-1">Current</div>
                        <div
                            data-testid={`approval-current-${a.id}`}
                            className="rounded-xl bg-white/[0.02] border border-white/5 p-3 text-sm text-slate-300 min-h-[80px] whitespace-pre-wrap break-words"
                        >
                            {p[cfg.current] || "—"}
                        </div>
                    </div>
                    <div className="min-w-0">
                        <div className="label-eyebrow text-indigo-300 mb-1">Proposed</div>
                        {editor}
                    </div>
                </div>
            ) : (
                editor
            )}

            {!isPending && p.applied_state && (
                <div className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 p-3 text-xs text-emerald-200">
                    {p.applied_state}
                </div>
            )}

            {isPending && (
                <div className="flex items-center gap-2 flex-wrap">
                    {editable && (
                        <>
                            <Button
                                onClick={() => decide("approve")}
                                disabled={busy || !text.trim()}
                                data-testid={`approval-item-approve-button-${a.id}`}
                                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-1.5"
                            >
                                <CheckCircle2 className="h-4 w-4" /> {cfg.publish ? "Approve & publish" : "Approve"}
                            </Button>
                            <Button
                                onClick={() => decide("edit")}
                                disabled={busy || !text.trim()}
                                data-testid={`approval-item-edit-button-${a.id}`}
                                variant="outline"
                                className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5"
                            >
                                <Pencil className="h-4 w-4" /> {cfg.publish ? "Save edits & publish" : "Save edits & approve"}
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={() => decide("reject")}
                        disabled={busy}
                        data-testid={`approval-item-reject-button-${a.id}`}
                        variant="outline"
                        className="border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 gap-1.5"
                    >
                        <XCircle className="h-4 w-4" /> {a.is_stale ? "Dismiss" : "Reject"}
                    </Button>
                </div>
            )}
        </div>
    );
}
